/**
 * /zh-tw/product-roadmap — 產品路線圖 concept page (Traditional Chinese).
 * Links /zh-tw/product-discovery + /zh-tw/decision-log.
 */
import { MarketingShell } from './MarketingShell';

export function ZhTwProductRoadmap() {
  return (
    <MarketingShell
      currentLocale="zhtw"
      hreflangPath="/product-roadmap"
      hreflangLocales={['zhtw']}
      documentTitle="產品路線圖怎麼做｜範本、範例與實務 · Draftlize"
      documentDescription="產品路線圖（product roadmap）的做法、格式與範例，並教你打造能因應範圍變動、不會過期的「活的路線圖」。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 路線圖</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            能扛住範圍變動的<br />
            <em>產品路線圖。</em>
          </h1>
          <p className="lede">
            產品路線圖告訴大家產品<em>往哪裡走</em>、以及<em>為什麼</em>——不是每個細節，而是方向與順序。難的往往不是把它做出來，而是它最後變成一張「凍住的簡報」：三週後決策變了，路線圖沒變，於是沒人再相信它。這頁講格式與範例，以及怎麼把路線圖接到決策、讓它「一直為真」。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">實際好用的路線圖格式。</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>Now / Next / Later</strong>——避免假的日期精確度，早期最適合。</li>
            <li><strong>主題／成果導向</strong>——依目標排，而非功能。</li>
            <li><strong>時間軸（季度）</strong>——當依賴讓期限變硬時使用。</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            <strong>迷你範例（Now/Next/Later）:</strong> Now — 降低上手摩擦 · Next — 自助訂閱付費 · Later — 團隊工作區。每一項都帶著<em>為什麼</em>——把它放上路線圖的那個決策。簡報版丟掉的，正是這個「為什麼」。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">凍住 vs 活的</span>
            <h2 className="serif">簡報路線圖 <em>vs 隨決策而動的路線圖。</em></h2>
          </div>
          <p>路線圖只是一連串決策「看得見的尖端」。把兩者切開，它就會悄悄過期。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">連動的路線圖（Draftlize）</th>
              <th>簡報路線圖</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>每一項的「為什麼」</td><td className="ours yes">連到決策卡片</td><td className="no">在腦裡／散落</td></tr>
            <tr><td>決策改變時</td><td className="ours yes">受影響項目變 stale</td><td className="no">路線圖還是舊的</td></tr>
            <tr><td>三週後</td><td className="ours yes">是否最新一目了然</td><td className="no">沒人相信</td></tr>
            <tr><td>AI agent 能用</td><td className="ours yes">用 MCP 讀脈絡</td><td className="partial">只是平的簡報</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          大多數路線圖不是規劃錯了，只是從上次決策後的三週，沒人讓它跟著更新。
        </blockquote>
        <cite>把路線圖接到決策，決策一變它就自動跟上。這就是 Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常見問題</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '路線圖和 backlog 差在哪？', a: '路線圖以成果為單位，講方向與順序；backlog 是具體工作的優先清單。路線圖回答「為什麼／往哪」，backlog 回答「下一個做什麼」。' },
            { q: '時間範圍多長？', a: '到你能誠實給出方向為止。比起固定季度，Now/Next/Later 往往更有效。假的日期精確度，是路線圖失去可信度的主因。' },
            { q: '範圍變動怎麼處理？', a: '某項背後的決策變了，這項也該變。在 Draftlize 兩者連動：變動的決策會自動把受影響的路線圖部分標記 stale。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
        <p style={{ maxWidth: 820, margin: '8px auto 0', fontSize: 15, color: 'var(--ink-1)' }}>
          相關： <a href="/zh-tw/product-discovery" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>產品探索</a> · <a href="/zh-tw/decision-log" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>決策紀錄</a>
        </p>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">一張會自動跟上的路線圖<em>——免費送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新註冊送 $5</span>
          <span>用多少付多少</span>
          <span>餘額永不過期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            用連動的卡片做路線圖，而不是簡報。決策一變，路線圖自動跟上——不是三週後。
          </p>
          <a className="btn-primary" href="/">免費開始，送 $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
